import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import WebApi from '../services/webApi';
import { IS_WEB } from '../services/platform';
import { hasTrialIntent } from '../shared/trialIntent';

// Renderer-side view of the license state. On desktop the Electron main
// process is the source of truth; on web builds it's the ppm-app worker.
// Either way this context only mirrors state for UI — enforcement happens
// on the authoritative side.

const LicenseContext = createContext({
  state: null,
  promptOpen: false,
  openPrompt: () => {},
  closePrompt: () => {},
  reload: async () => {},
  startTrial: async () => ({}),
  activate: async () => ({}),
  recheck: async () => ({}),
  deactivate: async () => ({}),
  cancelSubscription: async () => ({}),
  buy: async () => ({})
});

export const useLicense = () => useContext(LicenseContext);

// Shown on the purchase screen; amounts must match the license server's PLANS.
export const PRICING = [
  { id: 'monthly', name: 'Monthly', price: 'R65', per: 'per month', note: 'Cancel anytime' },
  { id: 'annual', name: 'Annual', price: 'R570', per: 'per year', note: '3 months free vs monthly', highlight: true },
  { id: 'lifetime', name: 'Lifetime', price: 'R2,700', per: 'once-off', note: 'Pay once, use forever' }
];

export function LicenseProvider({ children }) {
  const [state, setState] = useState(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const trialIntent = IS_WEB && hasTrialIntent();

  const reload = useCallback(async () => {
    try {
      if (window.electronAPI && window.electronAPI.getLicenseState) {
        setState(await window.electronAPI.getLicenseState());
      } else if (IS_WEB) {
        setState(await WebApi.licenseState());
      } else {
        // Browser development mode — no licensing.
        setState({ mode: 'full', plan: 'dev', status: 'active', devBypass: true, trial: null });
      }
    } catch (error) {
      console.error('Could not load license state:', error);
      setState({ mode: 'error' });
    }
  }, []);

  useEffect(() => {
    reload();
    if (window.electronAPI && window.electronAPI.onLicenseStateChanged) {
      window.electronAPI.onLicenseStateChanged((next) => setState(next));
    }
    return () => {
      if (window.electronAPI && window.electronAPI.removeAllListeners) {
        window.electronAPI.removeAllListeners('license-state-changed');
      }
    };
  }, [reload]);

  // A fresh, unlicensed install must decide (trial / buy / key) before use.
  // A marketing trial link also opens the prompt for an expired account so
  // the user immediately sees that they need to choose a paid plan.
  useEffect(() => {
    if (state && (state.mode === 'unlicensed' || (trialIntent && state.mode === 'readonly'))) {
      setPromptOpen(true);
    }
  }, [state, trialIntent]);

  // Each license IPC returns the fresh state (and the main process also
  // broadcasts it). Apply the returned state directly rather than firing a
  // second get-license-state that could resolve after a newer broadcast and
  // clobber it with stale data.
  const applyResult = (r) => {
    if (r && r.state) setState(r.state);
    return r;
  };

  // Desktop actions go over IPC; web actions hit the app API. Both return
  // { ..., state } so applyResult keeps the UI in sync either way.
  const desktop = () => Boolean(window.electronAPI);
  // Desktop IPC handlers never reject — they resolve { success: false, error }.
  // Callers (Settings, LicenseBanner) rely on that, so web calls must keep
  // the same contract instead of throwing.
  const webCall = async (fn) => {
    try {
      return await fn();
    } catch (error) {
      return { success: false, error: error.message || 'Something went wrong. Please try again.' };
    }
  };
  const value = {
    state,
    promptOpen,
    openPrompt: () => setPromptOpen(true),
    closePrompt: () => setPromptOpen(false),
    reload,
    startTrial: async () =>
      applyResult(desktop() ? await window.electronAPI.startTrial() : await webCall(() => WebApi.trialStart())),
    activate: async (key) =>
      applyResult(desktop() ? await window.electronAPI.activateLicense(key) : await webCall(() => WebApi.activate(key))),
    recheck: async () =>
      applyResult(desktop() ? await window.electronAPI.refreshLicense() : await webCall(() => WebApi.refresh())),
    deactivate: async () =>
      applyResult(desktop() ? await window.electronAPI.deactivateLicense() : await webCall(() => WebApi.deactivate())),
    cancelSubscription: async () =>
      applyResult(desktop() ? await window.electronAPI.cancelSubscription() : await webCall(() => WebApi.cancelSubscription())),
    buy: async (plan) => {
      if (desktop()) return window.electronAPI.openCheckout(plan);
      // Open the tab synchronously inside the click's user activation —
      // window.open after an await gets popup-blocked in Safari.
      const popup = window.open('about:blank', '_blank');
      try {
        const url = await WebApi.buyUrl(plan);
        if (popup && !popup.closed) {
          popup.location.href = url;
          return { success: true };
        }
        // Primary popup was blocked; this post-await attempt usually is too —
        // report honestly rather than leaving the UI "awaiting payment".
        const fallback = window.open(url, '_blank', 'noopener');
        if (fallback) return { success: true };
        return {
          success: false,
          error: 'Your browser blocked the checkout window. Allow pop-ups for this site and try again.'
        };
      } catch (error) {
        if (popup) popup.close();
        return { success: false, error: error.message || 'Could not open the checkout page.' };
      }
    }
  };

  return <LicenseContext.Provider value={value}>{children}</LicenseContext.Provider>;
}

export default LicenseContext;
